import { useState, isValidElement, cloneElement } from "react";
import { useTranslation } from "react-i18next";
import { Popover, PopoverTrigger, PopoverContent } from "@workspace/ui/components/ui/popover";
import { DsIcon, type DsIconName } from "@/components/ui/ds-icons";
import { cn } from "@/lib/utils";

export type RentalType = "driving" | "chauffeured" | "event";

export interface RentalTypeOption {
  key: RentalType;
  label: string;
  available: boolean;
}

interface Props {
  trigger: React.ReactNode;
  current: RentalType;
  options: RentalTypeOption[];
  onSelect: (next: RentalType) => void;
}

const tabIconMap: Record<RentalType, DsIconName> = {
  driving: "driveable",
  chauffeured: "chauffeured",
  event: "event-rental",
};

export function CheckoutRentalTypeEditor({ trigger, current, options, onSelect }: Props) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const available = options.filter((o) => o.available);

  // If there are no available types, or the sole available type is already
  // selected, render the trigger inert so the row still looks consistent.
  // We strip any onClick to avoid "Change" doing nothing on click.
  // Keep the picker interactive when the current URL type is no longer
  // available but another type is: this lets checkout recover stale/deep
  // links (including a newly blocked New York self-drive request).
  if (available.length === 0 || (available.length === 1 && available[0].key === current)) {
    if (isValidElement(trigger)) {
      const props = trigger.props as Record<string, unknown> | null | undefined;
      const baseClass = (props && typeof props.className === "string") ? props.className : "";
      return cloneElement(trigger as React.ReactElement<Record<string, unknown>>, {
        onClick: undefined,
        disabled: true,
        "aria-disabled": true,
        className: cn(baseClass, "pointer-events-none opacity-40"),
      });
    }
    return <>{trigger}</>;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-72 rounded-xl border border-border bg-popover p-3 shadow-xl"
      >
        <p className="mb-2 px-1 text-xs font-semibold text-foreground">
          {t("checkout.switch_rental_type", "Switch rental type")}
        </p>
        <div className="space-y-1.5">
          {available.map((tab) => {
            const isCurrent = tab.key === current;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => {
                  if (!isCurrent) onSelect(tab.key);
                  setOpen(false);
                }}
                className={cn(
                  "flex w-full items-center gap-2.5 rounded-md border px-3 py-2 text-left text-sm transition-colors",
                  isCurrent
                    ? "border-foreground bg-muted/40"
                    : "border-border hover:bg-muted/50",
                )}
              >
                <DsIcon name={tabIconMap[tab.key]} className="h-5 w-5" />
                <span className="font-medium text-foreground">{tab.label}</span>
                {isCurrent && (
                  <span className="ml-auto text-[10px] uppercase tracking-wide text-muted-foreground">
                    {t("checkout.rental_type_current", "current")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default CheckoutRentalTypeEditor;
